/**
 * History-based routing over a flat table of paths.
 *
 * It resolves a path to a route and notifies subscribers; it never touches the DOM. Rendering is the
 * shell element's job, and each view element owns its own loading lifecycle - which is why this is
 * synchronous and has no cancellation to manage.
 *
 * ## Two bundles on one origin
 *
 * This service ships two applications: the administration SPA under `/admin`, and the end-user SPA
 * everywhere else. They share this router, the platform half and the session, and they are separate
 * *bundles* - which is the whole point, since an end user should never be sent the administrative
 * screens at all.
 *
 * So a router has to know which paths belong to the bundle it is running in. {@link startRouter} takes
 * an `owns` predicate, and a navigation to a path this bundle does not own becomes a **real page load**
 * rather than a route lookup that would land on the catch-all. Getting that wrong is not subtle: signing
 * in and being sent to `/admin` would render "not found" inside the user bundle, on an origin where the
 * page exists.
 *
 * There are no path parameters in this application, and that is a decision rather than an omission: a
 * page's subject is a **named** query parameter (`/records?sourcetype=licenses`), so no address bar
 * carries an unlabelled value that a reader has to know the shape of. The matcher below still compiles
 * `:name` segments, because a route table is not the place to enforce that; the route table's own test
 * is what refuses a path containing one.
 */

import { notifyUrlChange } from './shared/state/urlState.js';

let routes = [];
let guard = null;
let owns = null;
let match = null;

const listeners = new Set();

function compile(path)
{
   const names = [];

   // Segments are escaped one at a time: escaping the whole path first and then substituting would have
   // to match an escaped separator, and '/' is not a regex metacharacter, so there is nothing to match.
   const pattern = path.split('/').map(segment =>
   {
      if (segment.startsWith(':'))
      {
         names.push(segment.slice(1));
         return '([^/]+)';
      }

      return segment.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
   }).join('/');

   return { matcher: new RegExp(`^${pattern}/?$`), names };
}

function resolve(pathname)
{
   for (const route of routes)
   {
      const found = route.matcher.exec(pathname);
      if (!found)
      {
         continue;
      }

      const params = {};
      route.names.forEach((name, index) => { params[name] = decodeURIComponent(found[index + 1]); });

      return { route, params };
   }

   // The catch-all is resolved here rather than after the guard, so an unknown path is subject to the
   // same authentication rules as a known one and the shell gets a title for it.
   return { route: routes.find(route => route.path === '*'), params: {} };
}

function update()
{
   // Before anything else: a path this bundle does not serve is not something to resolve, it is
   // somewhere else on the origin. Replacing rather than assigning would leave the address bar pointing
   // at the other application while this one rendered its catch-all.
   if (owns && !owns(location.pathname))
   {
      location.assign(location.pathname + location.search);
      return match;
   }

   const resolved = resolve(location.pathname);
   const target = guard?.(resolved.route, location.pathname);

   if (target && target !== location.pathname + location.search)
   {
      if (owns && !owns(target.split('?')[0]))
      {
         // The guard sent us to the other bundle - the shared sign-in page is the case this exists for.
         location.assign(target);
         return match;
      }

      history.replaceState({}, '', target);
      return update();
   }

   match = {
      route: resolved.route,
      params: resolved.params,
      query: new URLSearchParams(location.search),
      path: location.pathname
   };

   for (const listener of listeners)
   {
      listener(match);
   }

   // A navigation is an address-bar change like any other, and the query string is where a page's
   // subject lives. Without this, following a link to `?sourcetype=repositories` from a page already
   // showing `?sourcetype=licenses` re-renders the same element - so nothing reconnects, nothing
   // re-reads its filters, and the page keeps the previous subject under the new URL.
   notifyUrlChange();

   return match;
}

export function currentMatch()
{
   return match;
}

/** Programmatic navigation. Navigating to the current path re-notifies, which is what a reload wants. */
export function navigate(path, { replace = false } = {})
{
   // A cross-bundle link is a page load. See the note on `owns` above.
   if (owns && !owns(String(path).split('?')[0]))
   {
      location.assign(path);
      return match;
   }

   if (replace)
   {
      history.replaceState({}, '', path);
   }
   else
   {
      history.pushState({}, '', path);
   }

   return update();
}

export function subscribe(listener)
{
   listeners.add(listener);
   return () => listeners.delete(listener);
}

/**
 * @param {object} options
 * @param {Array<{path: string, title: string, render: Function}>} options.routes
 * @param {Function} [options.guard]  Returns a path to redirect to, or nothing to proceed.
 * @param {Function} [options.owns]   Whether a pathname belongs to this bundle. Anything else is a page
 *    load - see the note on two bundles above.
 */
export function startRouter(options)
{
   routes = options.routes.map(route => ({ ...route, ...compile(route.path) }));
   guard = options.guard;
   owns = options.owns;

   addEventListener('popstate', () => update());

   // One delegated listener beats wiring every link: anchors carry a relative href and the kit's own
   // navigation events are handled separately, in <app-root>.
   addEventListener('click', event =>
   {
      const anchor = event.target.closest?.('a[href^="/"]');
      if (!anchor || anchor.target === '_blank' || anchor.hasAttribute('download') ||
         event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0)
      {
         return;
      }

      const href = anchor.getAttribute('href');

      // Left to the browser when it is the other bundle's, which is what makes the link work at all.
      if (owns && !owns(href.split('?')[0]))
      {
         return;
      }

      event.preventDefault();
      navigate(href);
   });

   return update();
}
